import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Reveal } from "../ui/Reveal";
import { SectionHeading } from "../ui/SectionHeading";
import { FAQS } from "../../content/site";

export function FAQAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="relative bg-cream py-20 sm:py-28">
      <div className="container-x">
        <SectionHeading
          eyebrow="Questions"
          title={
            <>
              Everything you're <span className="marker-teal marker">wondering</span>
            </>
          }
          align="center"
        />

        <div className="mx-auto mt-14 max-w-3xl">
          {FAQS.map((faq, i) => {
            const isOpen = openIndex === i;
            const panelId = `faq-panel-${i}`;
            const buttonId = `faq-button-${i}`;

            return (
              <Reveal key={faq.q} delay={i} className="mb-4">
                <div
                  className={`rounded-[1.5rem] border-[3px] border-navy transition-all duration-300 ${
                    isOpen ? "bg-sun-pale shadow-pop" : "bg-white shadow-pop-sm"
                  }`}
                >
                  <h3>
                    <button
                      id={buttonId}
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setOpenIndex(isOpen ? null : i)}
                      className="flex w-full items-center justify-between gap-4 rounded-[1.5rem] p-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2 focus-visible:ring-offset-cream sm:p-6"
                    >
                      <span className="font-display text-base font-extrabold text-navy sm:text-lg">
                        {faq.q}
                      </span>
                      <motion.span
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border-[3px] border-navy bg-sun text-navy shadow-pop-xs"
                        aria-hidden="true"
                      >
                        <ChevronDown className="h-5 w-5" strokeWidth={3} />
                      </motion.span>
                    </button>
                  </h3>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        id={panelId}
                        role="region"
                        aria-labelledby={buttonId}
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="px-5 pb-6 leading-relaxed text-charcoal/75 sm:px-6">
                          {faq.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mx-auto mt-10 max-w-3xl text-center">
          <p className="text-sm font-medium text-charcoal/70">
            Still curious?{" "}
            <a
              href="#contact"
              className="font-display font-extrabold text-navy underline decoration-teal decoration-[3px] underline-offset-4 hover:decoration-sun"
            >
              Ask us anything
            </a>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
